// Customizable Area Start
import { IBlock } from "../../../framework/src/IBlock";
import { Message } from "../../../framework/src/Message";
import { BlockComponent } from "../../../framework/src/BlockComponent";
import { runEngine } from "../../../framework/src/RunEngine";
import MessageEnum, {
  getName,
} from "../../../framework/src/Messages/MessageEnum";
import * as Yup from "yup";
import { InfoType } from "../../../components/src/CustomInfoDialog.web";
export const configJSON = require("./config");

export const passwordValidations = [
  {
    rule: /[A-Z]/,
    label: "At least one capital letter (A-Z)",
  },
  {
    rule: /[a-z]/,
    label: "At least one lowercase letter (a-z)",
  },
  {
    rule: /[@$!%*?&]/,
    label: "At least one special character",
  },
  {
    rule: /\d/,
    label: "At least one number (0-9)",
  },
  {
    rule: /.{8,}/,
    label: "Minimum length is 8 characters",
  },
];

interface IResetPasswordValues {
  newPassword: string;
  confirmPassword: string;
}

interface IResetPasswordPayload {
  password: string;
  password_confirmation: string;
  reset_token: string;
}

interface IResetPasswordSuccessResponse {
  message: string;
}

interface IResetPasswordErrorResponse {
  alert: string;
}

export const validationSchema = Yup.object().shape({
  newPassword: Yup.string()
    .required("New password is required")
    .max(20, "New password must be at most 20 characters"),
  confirmPassword: Yup.string()
    .required("Confirm password is required")
    .when("newPassword", {
      is: (newPassword: string) => newPassword && newPassword.length > 7,
      then: (schema: Yup.StringSchema) =>
        schema
          .required("Confirm password is required")
          .oneOf(
            [Yup.ref("newPassword")],
            "Passwords do not match. Please make sure both passwords are identical."
          ),
    }),
});
// Customizable Area End

export interface Props {
  navigation: any;
  id: string;
  // Customizable Area Start
  // Customizable Area End
}

export interface S {
  // Customizable Area Start
  isLoading: boolean;
  apiError: string;
  isInfoDialog: boolean;
  infoType: InfoType;
  infoMessage: string;
  // Customizable Area End
}

export interface SS {
  // Customizable Area Start
  id: any;
  // Customizable Area End
}

export default class ResetPasswordController extends BlockComponent<
  Props,
  S,
  SS
> {
  // Customizable Area Start
  redirectTimer: number | null = null
  resetPasswordApiCallId: string = "";
  // Customizable Area End

  constructor(props: Props) {
    super(props);
    this.subScribedMessages = [
      getName(MessageEnum.RestAPIResponceMessage),
      getName(MessageEnum.NavigationPayLoadMessage),
      getName(MessageEnum.CountryCodeMessage),
    ];
    this.receive = this.receive.bind(this);

    runEngine.attachBuildingBlock(this as IBlock, this.subScribedMessages);

    this.state = {
      // Customizable Area Start
      isLoading: false,
      apiError: "",
      isInfoDialog: false,
      infoType: "success",
      infoMessage: "",
      // Customizable Area End
    };

    // Customizable Area Start
    // Customizable Area End
  }

  async receive(from: string, message: Message) {
    // Customizable Area Start
    if (getName(MessageEnum.RestAPIResponceMessage) === message.id) {
      const apiRequestCallId = message.getData(
        getName(MessageEnum.RestAPIResponceDataMessage)
      );

      const responseJson = message.getData(
        getName(MessageEnum.RestAPIResponceSuccessMessage)
      );

      if (apiRequestCallId === this.resetPasswordApiCallId) {
        this.handleResetPasswordResponse(responseJson);
      }
    }
    // Customizable Area End
  }

  // Customizable Area Start
  async componentDidMount(): Promise<void> {
    this.handleCheckTokenExist();
  }

  getTokenFromUrl = () => {
    const params = new URLSearchParams(window.location.search);
    return params.get(configJSON.RESET_PASSWORD_TOKEN) || "";
  };

  handleCheckTokenExist = () => {
    const verificationToken = this.getTokenFromUrl();

    if (!verificationToken) {
      this.openInfoDialog("error");
    }
  };

  isSubmitDisabled = (values: IResetPasswordValues) => {
    const { newPassword, confirmPassword } = values;
    const { isLoading } = this.state;
    const isAllTestPassed = passwordValidations.every(({ rule }) =>
      rule.test(newPassword)
    );
    const isValid =
    !isLoading && isAllTestPassed && newPassword && confirmPassword;
    return !isValid;
  };

  openInfoDialog = (infoType: InfoType, infoMessage: string = "") => {
    this.setState({
      isLoading: false,
      isInfoDialog: true,
      infoType,
      infoMessage,
    });
  };

  closeInfoDialog = () => {
    this.setState({
      isInfoDialog: false,
    });
    this.handleRedirection("EmailAccountLoginBlock")
  };

  handleResetPasswordSubmit = (values: IResetPasswordValues) => {
    const { newPassword, confirmPassword } = values;
    this.setState({ isLoading: true, apiError: "" });
    const token = this.getTokenFromUrl();
    this.resetPasswordApi({
      password: newPassword,
      password_confirmation: confirmPassword,
      reset_token: token,
    });
  };

  handleRedirection = (path: string) => {
    const navigation = new Message(getName(MessageEnum.NavigationMessage));
    navigation.addData(getName(MessageEnum.NavigationTargetMessage), path);
    navigation.addData(getName(MessageEnum.NavigationPropsMessage), this.props);
    this.send(navigation);
  };

  resetPasswordApi = (httpBody: IResetPasswordPayload) => {
    const header = {
      "Content-Type": configJSON.loginApiContentType,
    };

    const requestMessage = new Message(
      getName(MessageEnum.RestAPIRequestMessage)
    );

    this.resetPasswordApiCallId = requestMessage.messageId;

    requestMessage.addData(
      getName(MessageEnum.RestAPIResponceEndPointMessage),
      configJSON.resetPasswordApiEndpoint
    );

    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestHeaderMessage),
      JSON.stringify(header)
    );

    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestBodyMessage),
      JSON.stringify(httpBody)
    );

    requestMessage.addData(
      getName(MessageEnum.RestAPIRequestMethodMessage),
      configJSON.httpPostMethod
    );

    runEngine.sendMessage(requestMessage.id, requestMessage);
    return true;
  };

  handleResetPasswordResponse = (
    responseJson: IResetPasswordSuccessResponse | IResetPasswordErrorResponse
  ) => {
    if ("message" in responseJson) {
      this.openInfoDialog("success", configJSON.passwordResetSuccessfulMessage);
    } else {
      this.setState({ isLoading: false, apiError: responseJson.alert });
    }
  };
  // Customizable Area End
}
